import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  FileText,
  Users,
  Vault,
  FileSignature,
  Settings,
  Crown,
  Sparkles,
  Bell,
  Bot,
} from 'lucide-react';
import { usePlanGating, Feature } from '@/hooks/usePlanGating';
import { useNotifications } from '@/hooks/useNotifications';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SoloLogoIcon } from '@/components/ui/solo-logo';
import { NotificationsModal } from '@/components/modals/NotificationsModal';

interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
  feature?: Feature;
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'SOLO', href: '/solo', icon: Bot, feature: 'ai_cfo' },
  { name: 'Invoices', href: '/invoices', icon: FileText },
  { name: 'Clients', href: '/clients', icon: Users },
  { name: 'Tax Vault', href: '/tax-vault', icon: Vault, feature: 'tax_vault' },
  { name: 'Contracts', href: '/contracts', icon: FileSignature, feature: 'contracts' },
  { name: 'Settings', href: '/settings', icon: Settings },
];

export function NavRail() {
  const location = useLocation();
  const { profile } = useAuth();
  const { isFeatureLocked } = usePlanGating();
  const { unreadCount } = useNotifications();
  const [expanded, setExpanded] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  const getPlanIcon = () => {
    switch (profile?.plan) {
      case 'pro': return Crown;
      case 'business': return Sparkles;
      default: return null;
    }
  };

  const PlanIcon = getPlanIcon();

  return (
    <>
      {/* Rail */}
      <motion.nav
        onMouseEnter={() => setExpanded(true)}
        onMouseLeave={() => setExpanded(false)}
        className={cn(
          "hidden lg:flex flex-col fixed left-0 top-0 bottom-0 z-40",
          "bg-receipt border-r-2 border-foreground/10",
          "transition-all duration-300 ease-out",
          expanded ? "w-56" : "w-[72px]"
        )}
      >
        {/* Thermal paper texture overlay */}
        <div className="absolute inset-0 pointer-events-none opacity-50">
          <div className="w-full h-full thermal-paper dark:thermal-paper-dark" />
        </div>

        {/* Logo */}
        <div className="relative h-16 flex items-center justify-center border-b-2 border-foreground/10">
          <Link to="/dashboard" className="flex items-center gap-3">
            <SoloLogoIcon size="sm" />
            <AnimatePresence>
              {expanded && (
                <motion.span
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  className="font-mono font-black text-lg tracking-tight overflow-hidden whitespace-nowrap gradient-gold-text"
                >
                  SOLO
                </motion.span>
              )}
            </AnimatePresence>
          </Link>
        </div>

        {/* Nav Items */}
        <div className="relative flex-1 py-4 px-3 space-y-1">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href || 
              (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
            const isLocked = item.feature && isFeatureLocked(item.feature);
            
            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  "flex items-center gap-3 h-12 transition-all duration-200",
                  expanded ? "px-4" : "justify-center px-0",
                  isActive
                    ? "bg-primary/10 text-primary border-l-2 border-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary border-l-2 border-transparent",
                  isLocked && "opacity-50"
                )}
              >
                <item.icon className={cn(
                  "w-5 h-5 shrink-0",
                  isActive && "text-primary"
                )} />
                <AnimatePresence>
                  {expanded && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      className="font-mono text-xs font-bold uppercase tracking-wider overflow-hidden whitespace-nowrap"
                    >
                      {item.name}
                    </motion.span>
                  )}
                </AnimatePresence>
              </Link>
            );
          })}
        </div>

        {/* Notifications Button */}
        <div className="relative px-3 py-2">
          <button
            onClick={() => setShowNotifications(true)}
            className={cn(
              "flex items-center gap-3 h-12 w-full transition-all duration-200",
              expanded ? "px-4" : "justify-center px-0",
              "text-muted-foreground hover:text-foreground hover:bg-secondary relative border-l-2 border-transparent"
            )}
          >
            <div className="relative">
              <Bell className="w-5 h-5 shrink-0" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-4 h-4 flex items-center justify-center text-[10px] font-bold gradient-gold text-primary-foreground rounded-full px-1 font-mono">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </div>
            <AnimatePresence>
              {expanded && (
                <motion.span
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  className="font-mono text-xs font-bold uppercase tracking-wider overflow-hidden whitespace-nowrap"
                >
                  Alerts
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>

        {/* Plan Badge */}
        {PlanIcon && (
          <div className="relative p-3 border-t-2 border-foreground/10">
            <div className={cn(
              "flex items-center gap-3 h-12",
              "bg-primary/10 text-primary border-l-2 border-primary",
              expanded ? "px-4" : "justify-center px-0"
            )}>
              <PlanIcon className="w-5 h-5 shrink-0" />
              <AnimatePresence>
                {expanded && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    className="font-mono text-xs font-bold uppercase tracking-wider overflow-hidden whitespace-nowrap"
                  >
                    {profile?.plan}
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
          </div>
        )}
      </motion.nav>

      {/* Notifications Modal */}
      <NotificationsModal
        open={showNotifications}
        onClose={() => setShowNotifications(false)}
      />
    </>
  );
}
